"use client";

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface FitLabelProps {
  /** Anchor position along the track, as a percentage of its width. */
  pct: number;
  /** "center" centers the label on `pct`; "end" pins its right edge to `pct`. */
  anchor: "center" | "end";
  className?: string;
  children: ReactNode;
}

/**
 * A label positioned along a track at `pct`, floated left/right as needed to stay inside the
 * track, and wrapped onto multiple lines only when it is wider than the track itself. Because the
 * label is in flow, the track grows in height to fit the wrapped lines.
 */
export function FitLabel({ pct, anchor, className, children }: FitLabelProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState<number | null>(null);
  const [alignRight, setAlignRight] = useState(false);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const label = labelRef.current;
    if (!track || !label) return;

    const update = () => {
      const trackWidth = track.clientWidth;
      const labelWidth = label.offsetWidth;
      const anchorPx = (pct / 100) * trackWidth;
      const desired = anchor === "center" ? anchorPx - labelWidth / 2 : anchorPx - labelWidth;
      setLeft(Math.max(0, Math.min(desired, trackWidth - labelWidth)));
      // A label that fills the whole track has wrapped; right-align it when it overflowed the right edge.
      const wrapped = labelWidth >= trackWidth - 1;
      setAlignRight(wrapped && (anchor === "end" || pct > 50));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(track);
    observer.observe(label);
    return () => observer.disconnect();
  }, [pct, anchor]);

  // Until measured (SSR / first paint), approximate the position in CSS.
  const initialStyle =
    anchor === "center"
      ? { marginLeft: `${pct}%`, transform: "translateX(-50%)" }
      : { marginLeft: `${pct}%`, transform: "translateX(-100%)" };

  return (
    <div ref={trackRef} className="relative flex min-h-6 items-center">
      <div
        ref={labelRef}
        className={cn("max-w-full w-max shrink-0", alignRight && "text-right", className)}
        style={left === null ? initialStyle : { marginLeft: `${left}px` }}
      >
        {children}
      </div>
    </div>
  );
}
